/**
 * I handle Simple view's "Log a spend" (#476): choose (Scan / Type / Say)
 * → confirm (amount, what it was, which bank) → Saved. There is no formula or
 * units choice: the amount is always added as a rand spend to the chosen
 * expense, through the same logged-visit primitive the full view uses
 * (POST /budget/place-visits/ with bump=1). One client_ref per save, reused on
 * a retry, and a scanned receipt travels in the SAME request (#699).
 *
 * Scan reuses the web receipt reader (/budget/items/scan_receipt/); Say reuses
 * the web VoiceButton (browser speech, else the server transcriber) and then
 * /budget/items/quick_parse/ to fill amount, expense and bank. When the AI
 * cannot match an expense, an editable purchase-based new name is offered.
 *
 * Where: the shop is detected — a scanned or spoken shop name matched to your
 * saved shops (else found on the map near you), or the saved shop you are
 * standing at — and can always be changed: nearby shops, a search, "Find shops
 * near me", or no shop. The shop is saved with the spend and remembered on the
 * expense (link_place), and the expenses linked to it come first in "What it
 * was". "+ New expense" adds a new line (named here) with the spend on it.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api.js';
import { useAuth } from '../../App.jsx';
import { ensureAIConsent, useAI } from '../AIGate.jsx';
import VoiceButton from '../VoiceButton.jsx';
import { Icon, rand, randCents } from './viewMode.jsx';

const BANK_KEY = 'vault_simple_bank';
const parseAmt = (v) => parseFloat(String(v || '').replace(/[\s R]/g, '').replace(',', '.')) || 0;
const showAmt = (v) => (v == null || v === '' ? '' : (Number(v) || 0).toFixed(2).replace('.', ','));
const newRef = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID() : `sv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
const today = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const lastBank = () => { try { return Number(localStorage.getItem(BANK_KEY)) || null; } catch { return null; } };
const hasSpeech = () => typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
/** True when an AI reply is the gate's "could not run" payload, not a reading. */
const blocked = (d) => !d || d.ai_off === true || d.ai_consent_required === true;
const HERE_M = 150;                      // a saved shop this close counts as "you are here"
const farLabel = (m) => (m == null ? '' : m < 1000 ? `${Math.round(m / 10) * 10 || 10} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`);
const norm = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
/** A saved shop whose name matches the receipt/spoken shop name (either contains the other). */
const matchShop = (list, name) => {
  const n = norm(name);
  if (n.length < 3) return null;
  const first = n.split(' ')[0];
  return list.find((p) => { const pn = norm(p.name); return pn && (pn.includes(n) || n.includes(pn) || (first.length >= 4 && pn.split(' ')[0] === first)); }) || null;
};

export default function SimpleLog({ budgetId, expenses, banks, bankName, perDay, refresh, onClose }) {
  const { me } = useAuth() || {};
  const ai = useAI();
  const [stage, setStage] = useState('choose');   // choose | confirm | done
  const [src, setSrc] = useState('type');         // scan | type | say
  const [amt, setAmt] = useState('');
  const [itemId, setItemId] = useState(null);
  const [bankId, setBankId] = useState(null);
  const [pickOpen, setPickOpen] = useState(null); // 'where' | 'what' | 'bank' | null
  const [receipt, setReceipt] = useState(null);   // { file, url }
  const [merchant, setMerchant] = useState('');
  const [heard, setHeard] = useState('');
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);       // { amount, item, bank }
  const [lightbox, setLightbox] = useState(false);
  const fileRef = useRef(null);
  const sayRef = useRef(null);
  const heardRef = useRef('');
  const voiceState = useRef('idle');
  const clientRef = useRef(null);                 // one id per save, kept across retries
  const titleRef = useRef(null);
  // Where — detected, always changeable.
  const [fix, setFix] = useState(null);           // { lat, lng } when location is allowed
  const [shops, setShops] = useState([]);         // your saved shops (distanceMeters once there is a fix)
  const [place, setPlace] = useState(null);       // { id?, name, address, lat, lng, how }
  const [placeQ, setPlaceQ] = useState('');
  const [found, setFound] = useState([]);         // shops from the map search
  const [finding, setFinding] = useState(false);
  const [newName, setNewName] = useState(null);   // a string while "+ New expense" is chosen
  const placeChosen = useRef(false);              // the member picked Where — detection never overrides
  const edited = useRef({ amount: false, bank: false })
  const receiptResolved = useRef(false);
  const [receiptSummary, setReceiptSummary] = useState('');
  const itemChosen = useRef(false);               // an expense was picked (by hand or by the AI)
  const lookedUp = useRef('');                    // the receipt shop already searched on the map
  const created = useRef(null);                   // a new expense already made by a save that then failed

  const validBank = (id) => (id && banks.some((b) => b.id === id) ? id : null);
  const bankFor = (item) => validBank(item?.bank) || validBank(lastBank()) || banks[0]?.id || null;
  const item = expenses.find((e) => e.id === itemId)
    || (created.current && created.current.id === itemId ? created.current : null);

  /** Expense ids linked to a shop: its home shop or one of its extra shops. */
  const linkedTo = (pl) => {
    if (!pl?.id) return [];
    const fromShop = pl.linkedExpenseIds || (shops.find((s) => s.id === pl.id) || {}).linkedExpenseIds || [];
    const fromItems = expenses.filter((e) => e.place === pl.id || (e.places || []).includes(pl.id)).map((e) => e.id);
    return [...new Set([...fromShop, ...fromItems])];
  };
  const linkedIds = linkedTo(place);
  const orderedExpenses = linkedIds.length
    ? [...expenses.filter((e) => linkedIds.includes(e.id)), ...expenses.filter((e) => !linkedIds.includes(e.id))]
    : expenses;
  const nearbyShops = shops.filter((s) => s.distanceMeters != null && s.distanceMeters <= 20000)
    .sort((a, b) => a.distanceMeters - b.distanceMeters);

  /** Your saved shops, then (when allowed) your location so they come nearest first. */
  const locate = () => {
    api('/budget/places/').then((d) => { if (Array.isArray(d)) setShops((cur) => (cur.length ? cur : d)); }).catch(() => {});
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((g) => {
      const p = { lat: g.coords.latitude, lng: g.coords.longitude };
      setFix(p);
      api(`/budget/places/?lat=${p.lat}&lng=${p.lng}`).then((d) => { if (Array.isArray(d)) setShops(d); }).catch(() => {});
    }, () => {}, { timeout: 8000, maximumAge: 300000, enableHighAccuracy: false });
  };
  const choosePlace = (pl, how) => {
    placeChosen.current = true;
    setPlace(pl ? { ...pl, how } : null);
    setPickOpen(null); setPlaceQ(''); setFound([]);
  };
  const autoPlace = (pl, how) => { if (!placeChosen.current && !receiptResolved.current) setPlace(pl ? { ...pl, how } : null); };

  // Detect Where: the receipt / spoken shop first, else the saved shop you are at.
  useEffect(() => {
    if (stage !== 'confirm' || placeChosen.current || receiptResolved.current) return;
    if (merchant) {
      const m = matchShop(shops, merchant);
      if (m) { autoPlace(m, 'receipt'); return; }
      if (fix && lookedUp.current !== merchant) {
        lookedUp.current = merchant;
        api('/budget/places/resolve/', { method: 'POST', body: { query: merchant, lat: fix.lat, lng: fix.lng } })
          .then((d) => {
            const c = (d?.candidates || [])[0];
            if (c && c.lat != null) autoPlace({ name: c.name, address: c.address || '', lat: c.lat, lng: c.lng, kind: c.source === 'address' ? 'address' : 'shop' }, 'receipt');
          }).catch(() => {});
      }
      return;
    }
    const here = nearbyShops[0];
    if (here && here.distanceMeters <= HERE_M) autoPlace(here, 'here');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, merchant, shops, fix]);

  // The expense linked to that shop comes first — and is picked if nothing else was.
  useEffect(() => {
    if (!place?.id || itemChosen.current || newName != null) return;
    const ids = linkedTo(place);
    const first = expenses.find((e) => ids.includes(e.id));
    if (first) { setItemId(first.id); setBankId(bankFor(first)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  // Search shops as you type (saved ones are filtered locally; the map search runs after a pause).
  useEffect(() => {
    if (pickOpen !== 'where') return undefined;
    const q = placeQ.trim();
    if (q.length < 2) { setFound([]); return undefined; }
    const t = setTimeout(async () => {
      setFinding(true);
      try {
        const d = await api('/budget/places/resolve/', { method: 'POST', body: { query: q, ...(fix || {}) } });
        setFound((d?.candidates || []).filter((c) => c.lat != null).slice(0, 6));
      } catch { setFound([]); } finally { setFinding(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [placeQ, pickOpen, fix]);

  const findNear = async () => {
    if (!fix) return;
    setFinding(true);
    try {
      const d = await api('/budget/places/resolve/', { method: 'POST', body: { lat: fix.lat, lng: fix.lng } });
      setFound((d?.candidates || []).filter((c) => c.lat != null).slice(0, 8));
    } catch { setFound([]); } finally { setFinding(false); }
  };

  useEffect(() => { titleRef.current?.focus(); }, [stage]);
  useEffect(() => () => { if (receipt?.url) URL.revokeObjectURL(receipt.url); }, [receipt]);

  /** Apply an AI reading (scan or quick_parse) to the form. */
  const fill = (d) => {
    if (d.mode) {
      // The server's shop (matched by the printed name and branch) is kept; with
      // no server shop the detection below still finds a saved shop by name or
      // the nearest match on the map (#738/#739), so Where fills automatically.
      receiptResolved.current = d.place != null;
      setReceiptSummary([d.description, d.item_reason].filter(Boolean).join(' · '));
      if (!placeChosen.current) {
        if (d.place != null) setPlace({ ...(shops.find((s) => String(s.id) === String(d.place)) || {}), id: d.place, name: d.place_name || d.title, how: 'receipt' });
        setPlaceQ([d.title, d.location].filter(Boolean).join(' '));
      }
      if (d.item == null && !itemChosen.current) setNewName(d.suggested_title || d.title || '');
    }
    if (!edited.current.amount && d.amount != null && d.amount !== '') setAmt(showAmt(parseAmt(d.amount)));
    const match = expenses.find((e) => e.id === Number(d.item));
    if (!itemChosen.current && match) {
      itemChosen.current = true;
      setItemId(match.id);
      if (!edited.current.bank) setBankId(bankFor(match));
      setPickOpen(null);
    } else if (!itemChosen.current) {
      setItemId(null);
      if (!edited.current.bank && validBank(Number(d.bank))) setBankId(Number(d.bank));
      setPickOpen('what');
    }
    const where = d.place_name || d.merchant || d.title || d.location || '';
    if (where) setMerchant(String(where).slice(0, 60));
  };

  const start = (s) => {
    setSrc(s); setAmt(''); setItemId(null); setBankId(bankFor(null)); setMerchant(''); setHeard('');
    heardRef.current = ''; setError(''); clientRef.current = null; setPickOpen(s === 'type' ? 'what' : null);
    setPlace(null); setPlaceQ(''); setFound([]); setNewName(null);
    edited.current = { amount: false, bank: false }; receiptResolved.current = false; setReceiptSummary(''); placeChosen.current = false; itemChosen.current = false; lookedUp.current = ''; created.current = null;
    setStage('confirm');
    locate();
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    start('scan');
    setReceipt({ file: f, url: URL.createObjectURL(f) });
    if (!ai.on || !(await ensureAIConsent(me))) { setPickOpen('what'); return; }
    setReading(true);
    try {
      const fd = new FormData();
      fd.append('receipt', f); fd.append('mode', 'receipt');
      const d = await api('/budget/items/scan_receipt/', { method: 'POST', form: fd });
      if (blocked(d)) setPickOpen('what'); else fill(d);
    } catch { setPickOpen('what'); } finally { setReading(false); }
  };

  const startSay = () => {
    // No browser speech and no AI to transcribe: fall back to typing it.
    if (!hasSpeech() && !ai.on) { start('type'); return; }
    setReceipt(null);
    start('say');
  };
  // Start listening as soon as the Say screen shows (still inside the tap's activation window).
  useEffect(() => {
    if (stage === 'confirm' && src === 'say') {
      const t = setTimeout(() => sayRef.current?.querySelector('button')?.click(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [stage, src]);

  const parseSaid = async (text) => {
    if (!ai.on || !(await ensureAIConsent(me))) { setPickOpen('what'); return; }
    setReading(true);
    try {
      const d = await api('/budget/items/quick_parse/', { method: 'POST', body: { text } });
      if (blocked(d)) setPickOpen('what'); else fill(d);
    } catch { setPickOpen('what'); } finally { setReading(false); }
  };
  const onVoiceState = (s) => {
    const was = voiceState.current;
    voiceState.current = s;
    if (s === 'idle' && (was === 'listening' || was === 'transcribing') && heardRef.current.trim()) {
      parseSaid(heardRef.current.trim());
    }
  };

  const pickItem = (e) => { itemChosen.current = true; setNewName(null); setItemId(e.id); setBankId(bankFor(e)); setPickOpen(null); };
  const pickNew = () => { itemChosen.current = true; setItemId(null); setNewName(''); setPickOpen(null); };
  const a = parseAmt(amt);
  const newOk = newName != null && newName.trim().length > 0;

  const save = async () => {
    if (reading || !a || !(item || newOk) || saving) return;
    setSaving(true); setError(''); setPickOpen(null);
    if (!clientRef.current) clientRef.current = newRef();
    const current = item;
    try {
      // A shop found on the map becomes one of your saved shops first (a retry reuses it).
      let placeId = place?.id || null;
      if (place && !placeId && place.lat != null) {
        const same = shops.find((s) => norm(s.name) === norm(place.name));
        const np = same || await api('/budget/places/', { method: 'POST', body: {
          name: String(place.name || 'Shop').slice(0, 120), address: String(place.address || '').slice(0, 255),
          lat: Number(place.lat).toFixed(6), lng: Number(place.lng).toFixed(6), kind: place.kind || 'shop' } });
        placeId = np.id;
        setPlace((cur) => (cur ? { ...cur, id: np.id } : cur));
      }
      // "+ New expense": make the line (with this bank and shop) and put the spend on it.
      let target = current;
      if (!target) {
        target = await api('/budget/items/', { method: 'POST', body: {
          budget: budgetId, kind: 'expense', title: newName.trim().slice(0, 80), amount: a.toFixed(2),
          source: 'manual', ...(bankId ? { bank: bankId } : {}), ...(placeId ? { place: placeId } : {}) } });
        created.current = target;
        setItemId(target.id); setNewName(null);
      }
      const item = target;
      // An expense with no bank yet takes the one picked here; a different bank
      // than the expense's own pays this spend itself (spend_bank).
      if (!item.bank && bankId) {
        await api(`/budget/items/${item.id}/`, { method: 'PATCH', body: { bank: bankId } });
      }
      const fields = {
        item: item.id, title: item.title, amount: a.toFixed(2), units: 0, on: today(),
        confirmed: true, bump: 1, client_ref: clientRef.current,
      };
      if (placeId) { fields.place = placeId; fields.link_place = 1; }
      if (item.bank && bankId && bankId !== item.bank) fields.spend_bank = bankId;
      if (receipt?.file) {
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v)));
        fd.append('receipt', receipt.file);
        await api('/budget/place-visits/', { method: 'POST', form: fd });
      } else {
        await api('/budget/place-visits/', { method: 'POST', body: fields });
      }
      const used = bankId || item.bank;
      try { if (used) localStorage.setItem(BANK_KEY, String(used)); } catch { /* private mode */ }
      clientRef.current = null;
      await refresh();
      setSaved({ amount: a, item: item.title, bank: bankName(used), where: place?.name || '' });
      setStage('done');
    } catch (e) {
      // The web has no offline queue: keep the form (same client_ref) so Save can simply be tapped again.
      setError(e?.status
        ? (e.data?.detail || 'Could not save — tap Save to try again.')
        : 'You’re offline, so this spend is not saved yet. Check your connection and tap Save again.');
    } finally { setSaving(false); }
  };

  if (stage === 'choose') {
    return (
      <div className="sv-form sv-fade">
        <h1 className="sv-h1" ref={titleRef} tabIndex={-1}>Log a spend</h1>
        <div className="sv-sub">How would you like to add it?</div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} />
        {[['scan', 'Scan a receipt', 'Take a photo of the slip', Icon.camera, () => fileRef.current?.click()],
          ['type', 'Type it', 'Enter the amount yourself', Icon.keyboard, () => { setReceipt(null); start('type'); }],
          ['say', 'Say it', 'Tell Vault what you bought', Icon.mic, startSay],
        ].map(([k, label, line, icon, go]) => (
          <button key={k} type="button" className="sv-tile" onClick={go}>
            <span className="sv-tile-icon">{icon}</span>
            <span style={{ minWidth: 0 }}>
              <span className="sv-tile-label">{label}</span>
              <span className="sv-tile-line">{line}</span>
            </span>
          </button>
        ))}
        <button type="button" className="sv-ghost" onClick={onClose}>Cancel</button>
      </div>
    );
  }

  if (stage === 'done' && saved) {
    return (
      <div className="sv-form">
        <div className="sv-done sv-fade" role="status">
          <div className="sv-done-icon">{Icon.check}</div>
          <div className="sv-done-title" ref={titleRef} tabIndex={-1}>Saved ✓</div>
          <div className="sv-done-line">
            {randCents(saved.amount)} added to {saved.item}{saved.where ? ` at ${saved.where}` : ''}{saved.bank ? ` · ${saved.bank}` : ''}
          </div>
          <div className="sv-done-safe">Safe to spend today is now {rand(perDay)}</div>
        </div>
        <button type="button" className="sv-primary mid" onClick={onClose}>Done</button>
      </div>
    );
  }

  const srcLine = src === 'scan'
    ? 'Read from your receipt — check it and tap Save.'
    : src === 'say' ? `You said: “${heard || '…'}”` : 'Type the amount, check the rest, and tap Save.';

  return (
    <div className="sv-form sv-fade">
      <h1 className="sv-h1" ref={titleRef} tabIndex={-1}>Is this right?</h1>
      {src === 'say' ? (
        <div className="sv-say">
          <div className="sv-accent-line" aria-live="polite">{srcLine}</div>
          <span ref={sayRef}>
            <VoiceButton size={48} title="Say it"
                         onText={(t) => { heardRef.current = t; setHeard(t); }}
                         onState={onVoiceState} />
          </span>
        </div>
      ) : (
        <div className="sv-accent-line">{srcLine}</div>
      )}

      {receipt && (
        <button type="button" className="sv-receipt" onClick={() => setLightbox(true)}>
          <img src={receipt.url} alt="" />
          <span style={{ minWidth: 0 }}>
            <span className="sv-receipt-title" style={{ display: 'block' }}>{merchant ? `Receipt · ${merchant}` : 'Receipt'}</span>
            <span className="sv-receipt-sub">Kept with this spend</span>
          </span>
        </button>
      )}

      {receiptSummary && <p className="sv-note">{receiptSummary}</p>}
      <div>
        <label className="sv-label" htmlFor="sv-amount" style={{ display: 'block' }}>Amount</label>
        <div className="sv-amount" aria-busy={reading ? 'true' : 'false'}>
          <span className="sv-amount-r" aria-hidden="true">R</span>
          <input id="sv-amount" inputMode="decimal" autoComplete="off" placeholder="0,00" value={amt}
                 onChange={(e) => { edited.current.amount = true; setAmt(e.target.value); }}
                 onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
          {reading && <span style={{ width: '1.75rem', height: '1.75rem', color: 'var(--sv-a300)' }}>{Icon.spinner}</span>}
        </div>
      </div>

      <div>
        <div className="sv-label" id="sv-where">Where</div>
        <button type="button" className="sv-pick" aria-labelledby="sv-where sv-where-name" aria-expanded={pickOpen === 'where'}
                onClick={() => setPickOpen(pickOpen === 'where' ? null : 'where')}>
          <span className="sv-pick-name" id="sv-where-name">
            {place ? place.name : 'No shop selected'}
            {place && (
              <span className="sv-pick-sub">
                {place.how === 'here' ? `You're here${place.distanceMeters != null ? ` · ${farLabel(place.distanceMeters)}` : ''}`
                  : place.how === 'receipt' ? (place.id ? 'From your receipt' : 'From your receipt · new shop')
                  : place.id ? (place.distanceMeters != null ? farLabel(place.distanceMeters) : 'Saved shop') : 'New shop'}
              </span>
            )}
          </span>
          <span className="sv-pick-change">Change</span>
        </button>
        {pickOpen === 'where' && (
          <div className="sv-opts sv-fade">
            <input className="sv-find" type="search" placeholder="Search a shop or place" aria-label="Search a shop or place"
                   value={placeQ} onChange={(e) => setPlaceQ(e.target.value)} autoComplete="off" />
            {(placeQ.trim().length >= 2
              ? shops.filter((s) => norm(s.name).includes(norm(placeQ)) || norm(s.address).includes(norm(placeQ))).slice(0, 5)
              : nearbyShops.slice(0, 5)
            ).map((s) => (
              <button key={`s${s.id}`} type="button" className="sv-opt sv-opt-2" aria-pressed={place?.id === s.id ? 'true' : 'false'}
                      onClick={() => choosePlace(s, 'saved')}>
                <span>{s.name}</span>
                <span className="sv-opt-sub">{s.distanceMeters != null ? farLabel(s.distanceMeters) : 'Saved shop'}</span>
              </button>
            ))}
            {found.map((c, i) => (
              <button key={`f${i}`} type="button" className="sv-opt sv-opt-2"
                      onClick={() => choosePlace({ name: c.name, address: c.address || '', lat: c.lat, lng: c.lng, kind: c.source === 'address' ? 'address' : 'shop' }, 'map')}>
                <span>{c.name}</span>
                <span className="sv-opt-sub">{c.address || 'On the map'}</span>
              </button>
            ))}
            {finding && <div className="sv-opt-sub" role="status" style={{ padding: '.5rem 1.125rem' }}>Looking…</div>}
            {fix && !placeQ.trim() && !finding && !found.length && (
              <button type="button" className="sv-opt" onClick={findNear}>Find shops near me</button>
            )}
            {!fix && !placeQ.trim() && (
              <div className="sv-opt-sub" style={{ padding: '.25rem 1.125rem' }}>Allow location to see the shops near you, or search above.</div>
            )}
            <button type="button" className="sv-opt" aria-pressed={!place ? 'true' : 'false'} onClick={() => choosePlace(null, 'none')}>No shop</button>
          </div>
        )}
      </div>

      <div>
        <div className="sv-label" id="sv-what">What it was</div>
        <button type="button" className="sv-pick" aria-labelledby="sv-what sv-what-name" aria-expanded={pickOpen === 'what'}
                onClick={() => setPickOpen(pickOpen === 'what' ? null : 'what')}>
          <span className="sv-pick-name" id="sv-what-name">
            {item ? item.title : newName != null ? (newName.trim() || 'New expense') : '—'}
            {newName != null && <span className="sv-pick-sub">New expense</span>}
          </span>
          <span className="sv-pick-change">Change</span>
        </button>
        {newName != null && (
          <input className="sv-find" style={{ marginTop: '.5rem' }} placeholder="Name it, e.g. Coffee" aria-label="Name of the new expense"
                 value={newName} maxLength={80} autoFocus onChange={(e) => setNewName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
        )}
        {pickOpen === 'what' && (
          <div className="sv-opts sv-fade">
            {orderedExpenses.map((e) => (
              <button key={e.id} type="button" className={`sv-opt${linkedIds.includes(e.id) ? ' sv-opt-2' : ''}`}
                      aria-pressed={e.id === itemId ? 'true' : 'false'} onClick={() => pickItem(e)}>
                <span>{e.title}</span>
                {linkedIds.includes(e.id) && <span className="sv-opt-sub">{place.name}{place.how === 'here' ? ' · nearest' : ''}</span>}
              </button>
            ))}
            <button type="button" className="sv-opt" aria-pressed={newName != null ? 'true' : 'false'} onClick={pickNew}>+ New expense</button>
          </div>
        )}
      </div>

      <div>
        <div className="sv-label" id="sv-bank">Which bank</div>
        <button type="button" className="sv-pick" aria-labelledby="sv-bank sv-bank-name" aria-expanded={pickOpen === 'bank'}
                onClick={() => setPickOpen(pickOpen === 'bank' ? null : 'bank')}>
          <span className="sv-pick-name" id="sv-bank-name">{bankName(bankId) || '—'}</span>
          <span className="sv-pick-change">Change</span>
        </button>
        {pickOpen === 'bank' && (
          <div className="sv-opts sv-fade">
            {banks.map((b) => (
              <button key={b.id} type="button" className="sv-opt" aria-pressed={b.id === bankId ? 'true' : 'false'}
                      onClick={() => { edited.current.bank = true; setBankId(b.id); setPickOpen(null); }}>{b.label}</button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="sv-error" role="alert">{error}</div>}
      <button type="button" className="sv-primary" disabled={!a || !(item || newOk) || saving || reading} onClick={save}>
        {saving && Icon.spinner}{saving ? 'Saving…' : 'Save'}
      </button>
      <button type="button" className="sv-ghost" style={{ minHeight: '3.25rem', fontSize: '1.0625rem' }} onClick={onClose}>Cancel</button>

      {lightbox && receipt && (
        <button type="button" className="sv-lightbox" aria-label="Close receipt" onClick={() => setLightbox(false)}>
          <img src={receipt.url} alt="Receipt" />
        </button>
      )}
    </div>
  );
}
